import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarDays } from 'lucide-react';
import { eventAPI, publicAPI } from '@/services/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { handleSubmit } from "@/utils/submitUtils";
import CostSummary from "@/components/CostSummary";
import ImageUpload from "@/components/ImageUpload";
import VideoUpload from "@/components/VideoUpload";
import FeatureAdToggle from "@/components/FeatureAdToggle";
import LocationEditor from "@/components/LocationEditor";
import SubmitButton from "@/components/SubmitButton";
import BackLink from "@/components/BackLink";
import { useAuth } from "@/contexts/AuthContext";
import WaiverCheckBox from "@/components/WaiverCheckBox";
import Section from "@/components/Section";
import { computeCosts, defaultPricing } from "@/utils/costUtil";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
	iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
	shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const EVENT_TYPES = ['competition', 'clinic', 'show', 'auction', 'meetup', 'race', 'course', 'other'];
const DISCIPLINES = ['dressage', 'show_jumping', 'vaulting', 'trail', 'western', 'classical', 'therapy',
	'natural_horsemanship', 'liberty', 'groundwork', 'colt_starting', 'young_horse', 'sports_therapy'];
const LEVELS = ['beginner', 'intermediate', 'advanced', 'competition'];
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function CreateEventAdPage() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const isClone = location.pathname.includes('clone');
	const { id: editId } = useParams();

	const [multiDay, setMultiDay] = useState(false);
	const [form, setForm] = useState({
		title: '',
		description: '',
		price: '',
		is_free: false,
		event_type: 'competition',
		start_date: '',
		end_date: '',
		start_time: '',
		disciplines: [],
		level: '',
		max_participants: '',
		prize_amount: '',
		registration_deadline: '',
		registration_url: '',
		website: '',
		contact_phone: '',
		location_name: '',
		location_lat: null,
		location_lng: null,
		is_featured: false,
		featured_paid: false,
		hidden_images: [],
		hidden_videos: []
	});
	const [saving, setSaving] = useState(false);
	const [pricing, setPricing] = useState(defaultPricing());
	const [waivedWithdrawalRight, setWaivedWithdrawalRight] = useState(false);

	// Media state: each item has { url?, file?, preview, enabled, isExisting }
	const [images, setImages] = useState([]);
	const [videos, setVideos] = useState([]);

	useEffect(() => {
		publicAPI.settings()
			.then(r => setPricing(prev => ({ ...prev, ...r.data })))
			.catch(() => {});
		if (editId) {
			eventAPI.get(editId).then(r => {
				const ad = r.data;
				setForm({
					title: ad.title || '',
					description: ad.description || '',
					price: ad.price || '',
					is_free: ad.is_free || false,
					event_type: ad.event_type || 'competition',
					start_date: ad.start_date || '',
					end_date: ad.end_date || '',
					start_time: ad.start_time || '',
					disciplines: ad.disciplines || [],
					level: ad.level || '',
					max_participants: ad.max_participants ||'',
					prize_amount: ad.prize_amount || '',
					registration_deadline: ad.registration_deadline || '',
					registration_url: ad.registration_url || '',
					website: ad.website || '',
					contact_phone: ad.contact_phone || '',
					contact_method: ad.contact_method || 'message',
					location_lat: ad.location_lat || '',
					location_lng: ad.location_lng || '',
					location_name: ad.location_name || '',
					is_featured: ad.is_featured || false,
					featured_paid: ad.featured_paid || false,
					hidden_images: ad.hidden_images || [],
					hidden_videos: ad.hidden_videos || [],
					included_images: ad.included_images || pricing.free_images_limit || 5,
					included_videos: ad.included_videos || pricing.free_videos_limit || 1,
				});
				// Load existing images as thumbnails
				const existingImages = (ad.images || []).map((url, i) => ({
					url,
					preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
					thumbnail: ad.thumbnails?.[i] ? (ad.thumbnails[i].startsWith('/') ? `${ API_BASE }${ ad.thumbnails[i] }` : ad.thumbnails[i]) : null,
					enabled: !(ad.hidden_images || []).includes(url),
					isExisting: true
				}));
				// need to remove images in case of cloning as I don't have the objects to upload
				setImages(isClone ? [] : existingImages);
				setMultiDay(ad.end_date !== '' && ad.end_date !== ad.start_date);
				// Load existing videos
				const existingVideos = (ad.videos || []).map(url => ({
					url,
					preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
					enabled: !(ad.hidden_videos || []).includes(url),
					isExisting: true,
					name: url.split('/').pop()
				}));
				// need to remove videos in case of cloning as I don't have the objects to upload
				setVideos(isClone ? [] : existingVideos);
			}).catch(() => navigate('/dashboard'));
		}
	}, [user, editId, navigate]);

	const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

	const toggleDiscipline = (value) => {
		setForm((p) => ({
			...p,
			disciplines: p.disciplines.includes(value) ? p.disciplines.filter((x) => x !== value) : [...p.disciplines, value],
		}));
	};

	// --- Image handlers ---
	const handleImages = (e) => {
		const files = Array.from(e.target.files);
		const newImgs = files.map(f => ({ file: f, preview: URL.createObjectURL(f), enabled: true, isExisting: false }));
		setImages(prev => [...prev, ...newImgs]);
	};
	const removeImage = (i) => {
		if (!images[i].isExisting) URL.revokeObjectURL(images[i].preview);
		setImages(prev => prev.filter((_, idx) => idx !== i));
	};
	const toggleImage = (i) =>
		setImages(prev => prev.map((img, idx) => idx === i ?
			{ ...img, enabled: !img.enabled } : img));

	// --- Video handlers ---
	const handleVideos = (e) => {
		const files = Array.from(e.target.files);
		const maxSize = (pricing.max_video_size_mb || 50) * 1024 * 1024;
		for (const f of files) {
			if (f.size > maxSize) {
				toast.error(t('horse.video_too_large', { size: pricing.max_video_size_mb }));
				return;
			}
		}
		const newVids = files.map(f =>
			({ file: f, preview: URL.createObjectURL(f), enabled: true, isExisting: false, name: f.name })
		);
		setVideos(prev => [...prev, ...newVids]);
	};
	const removeVideo = (i) => {
		if (!videos[i].isExisting) URL.revokeObjectURL(videos[i].preview);
		setVideos(prev => prev.filter((_, idx) => idx !== i));
	};
	const toggleVideo = (i) => setVideos(prev =>
		prev.map((v, idx) => idx === i ? { ...v, enabled: !v.enabled } : v)
	);

	const submit = async (e) => {
		e.preventDefault();
		if (!form.title.trim() || !form.description.trim() || !form.start_date) {
			toast.error(t('common.fill_required'));
			return;
		}
		if (multiDay && form.end_date && form.end_date < form.start_date) {
			toast.error(t('event.error_end_before_start'));
			return;
		}

		const cost = computeCosts(images, videos, pricing, form.is_featured, form.featured_paid, form.included_images, form.included_videos);

		const newImages = cost.enabledImages.filter(i => !i.isExisting);
		const newVideos = cost.enabledVideos.filter(v => !v.isExisting);

		// Compute hidden lists from existing media
		const hiddenImgs = images.filter(i => i.isExisting && !i.enabled).map(i => i.url);
		const hiddenVids = videos.filter(v => v.isExisting && !v.enabled).map(v => v.url);

		const payload = {
			...form,
			price: form.is_free ? 0 : (parseFloat(form.price) || 0),
			end_date: multiDay ? (form.end_date || null) : null,
			start_time: form.start_time || null,
			max_participants: form.max_participants ? parseInt(form.max_participants, 10) : null,
			prize_amount: form.prize_amount ? parseFloat(form.prize_amount) : null,
			registration_deadline: form.registration_deadline || null,
			registration_url: form.registration_url || null,
			level: form.level || null,
			location_lat: form.location_lat ? parseFloat(form.location_lat) : null,
			location_lng: form.location_lng ? parseFloat(form.location_lng) : null,
			included_images: Math.max(pricing.free_images_limit, cost.enabledImages.length),
			included_videos: Math.max(pricing.free_videos_limit, cost.enabledVideos.length),
			hidden_images: hiddenImgs,
			hidden_videos: hiddenVids,
		};

		await handleSubmit(payload, eventAPI, isClone ? null : editId, 'event', cost,
			newImages, newVideos,	form.is_featured, form.featured_paid, setSaving, t, navigate);
	};

	return (
		<div className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2]" data-testid="create-event-page">
			<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

				<BackLink title={form.title} uri="/events" textKey='nav.back_to_results' />

				<div className="flex items-center gap-3 mb-2">
					<CalendarDays className="w-6 h-6 text-[#8B4513]" />
					<h1 className="text-3xl font-medium text-[#1A3C34] font-['Playfair_Display']">
						{ isClone ? t('event.create_title') : editId ? t('event.edit_title') :
							t('event.create_title')}
					</h1>
				</div>
				<p className="text-sm text-[#666055] mb-6">{t('event.create_subtitle')}</p>

				<form onSubmit={submit} className="space-y-6"	data-testid="event-form">
					<Section title={t('horse.basic_info')}>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="md:col-span-2">
								<Label>{t('horse.title_label')} *</Label>
								<Input value={form.title} onChange={(e) => set('title', e.target.value)}
											 required maxLength={120} className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
											 data-testid="event-title" />
							</div>

							<div className="md:col-span-2">
								<Label>{t('horse.description')} *</Label>
								<Textarea value={form.description} onChange={(e) => set('description', e.target.value)}
													required rows={5} maxLength={4000}
													className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm text-sm mt-1"
													data-testid="event-description" />
							</div>

							<div>
								<Label>{t('event.event_type')} *</Label>
								<Select value={form.event_type} onValueChange={(v) => set('event_type', v)}>
									<SelectTrigger className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
																 data-testid="event-type-select">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{ EVENT_TYPES.map(u =>
											<SelectItem key={u} value={u}>{t(`event.type.${u}`)}
											</SelectItem>
										) }
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label>{t('event.level')}</Label>
								<Select value={form.level || 'none'} onValueChange={(v) => set('level', v === 'none' ? '' : v)}>
									<SelectTrigger className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1">
										<SelectValue placeholder={t('common.all')} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="none">{t('common.all')}</SelectItem>
										{ LEVELS.map(l =>
											<SelectItem key={l} value={l}>{t(`event.level_opt.${l}`)}</SelectItem>
										)}
									</SelectContent>
								</Select>
							</div>

							{/* Dates */}
							<div className="md:col-span-2">
								<div className="pt-2 border-t border-[#E6E2D6] mb-4">
									<div className="flex items-center justify-between mb-3">
										<Label className="inline-flex items-center gap-2">
											<CalendarDays className="w-4 h-4 text-[#8B4513]" />
											{t('event.when')} *
										</Label>
										<label className="flex items-center gap-2 cursor-pointer text-sm text-[#3a3a3a] select-none">
											<Checkbox checked={multiDay} onCheckedChange={(v) => setMultiDay(!!v)}
																data-testid="event-multi-day" />
											{t('event.multi_day')}
										</label>
									</div>
									<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
										<div>
											<Label className="text-xs text-[#666055]">{t('event.start_date')} *</Label>
											<Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)}
														 required className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
														 data-testid="event-start-date" />
										</div>
										{multiDay && (
											<div>
												<Label className="text-xs text-[#666055]">{t('event.end_date')}</Label>
												<Input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)}
															 min={form.start_date} className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
															 data-testid="event-end-date" />
											</div>
										)}
										<div>
											<Label className="text-xs text-[#666055]">{t('event.start_time')}</Label>
											<Input type="time" value={form.start_time} onChange={(e) => set('start_time', e.target.value)}
														 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
														 data-testid="event-start-time" />
										</div>
									</div>
								</div>
							</div>

						{/* Entry fee */}
						<div className="pt-2 border-t border-[#E6E2D6] md:col-span-2">
							<Label className="block mb-2">{t('event.entry_fee')}</Label>
							<div className="flex items-center gap-3 flex-wrap">
								<label className="flex items-center gap-2 cursor-pointer text-sm text-[#3a3a3a] select-none">
									<Checkbox checked={form.is_free} onCheckedChange={(v) => set('is_free', !!v)}
														data-testid="event-is-free" />
									{t('event.free_entry')}
								</label>
								{!form.is_free && (
									<div className="flex-1 min-w-[180px]">
										<Input type="number" min={0} step="any" value={form.price}
													 onChange={(e) => set('price', e.target.value)} placeholder="€"
													 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11" data-testid="event-price" />
									</div>
								)}
							</div>
						</div>
						</div>
					</Section>

					<Section title={t('event.disciplines')}>
						{/* Disciplines */}
						<div className="gap-4">
							<div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
								{DISCIPLINES.map(d => (
									<label key={d} className="flex items-center gap-2 cursor-pointer text-sm text-[#3a3a3a] select-none">
										<Checkbox checked={form.disciplines.includes(d)}
															onCheckedChange={() => toggleDiscipline(d)}
															data-testid={`event-discipline-${d}`} />
										{t(`trainer.discipline_opt.${d}`)}
									</label>
								))}
							</div>
						</div>
					</Section>

					<Section title={t('horse.details')}>
					{/* Extras */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<Label>{t('event.max_participants')}</Label>
							<Input type="number" min={1} value={form.max_participants}
										 onChange={(e) => set('max_participants', e.target.value)}
										 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
										 data-testid="event-max-participants" />
						</div>
						<div>
							<Label>{t('event.prize_amount')}</Label>
							<Input type="number" min={0} step="any" value={form.prize_amount}
										 onChange={(e) => set('prize_amount', e.target.value)} placeholder="€"
										 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
										 data-testid="event-prize-amount" />
						</div>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
						<div>
							<Label>{t('event.registration_deadline')}</Label>
							<Input type="date" value={form.registration_deadline}
										 onChange={(e) => set('registration_deadline', e.target.value)}
										 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
						</div>
						<div>
							<Label>{t('event.registration_url')}</Label>
							<Input value={form.registration_url} onChange={(e) => set('registration_url', e.target.value)}
										 placeholder="https://..." className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
										 data-testid="event-registration-url" />
						</div>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
						<div>
							<Label>{t('school.website')}</Label>
							<Input value={form.website} onChange={(e) => set('website', e.target.value)}
										 placeholder="https://..." className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
						</div>
					</div>

					<div>
						<Label>{t('horse.contact_phone')}</Label>
						<Input value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)}
									 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
					</div>
					</Section>

					{/* Location */}
					<LocationEditor
						location_name={ form.location_name }
						location_lat={ form.location_lat }
						location_lng={ form.location_lng }
						update={ set }/>

					{/* Images */}
					<ImageUpload images={ images }
											 pricing={ pricing }
											 handleImages={ handleImages }
											 removeImage={ removeImage }
											 toggleImage={ toggleImage } />

					{/* Videos */}
					<VideoUpload videos={ videos }
											 pricing={ pricing }
											 handleVideos={ handleVideos }
											 removeVideo={ removeVideo }
											 toggleVideo={ toggleVideo } />

					{/* Featured Ad */}
					<FeatureAdToggle is_featured={ form.is_featured } pricing={ pricing }  update={ set } />

					{/* Cost Summary */}
					<CostSummary
						images={ images }
						videos={videos}
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						t={ t }/>

					<WaiverCheckBox
						images={ images }
						videos={ videos }
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						onClick={ setWaivedWithdrawalRight }
						state={ waivedWithdrawalRight }
						t={ t }/>

					<SubmitButton
						editId={ isClone ? null : editId }
						loading={ saving }
						label='event.publish_btn'
						images={ images }
						videos={ videos }
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						onClick={ setWaivedWithdrawalRight }
						waivedWithdrawalRight={ waivedWithdrawalRight } />

				</form>
			</div>
		</div>
	);
}